import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  notification,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineUploadFile } from "react-icons/md";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  INITIAL_VALUES,
  TAG_COLORS,
  YAML_IMPORT_STRATEGIES,
} from "utils/constants";

const { TextArea } = Input;
const { Text, Title } = Typography;

const formatError = (error) => {
  const detail = get(error, "params.detail");
  const plan = get(error, "params.plan") || get(error, "params.identifier");
  const suffix = [plan && `plan: ${plan}`, detail && String(detail)]
    .filter(Boolean)
    .join(" — ");
  return suffix ? `${error.key} (${suffix})` : error.key;
};

const Line = ({ label, children }) => (
  <div className="yaml-import-drawer__line">
    <Text type="secondary" className="yaml-import-drawer__line-label">
      {label} :
    </Text>
    {children}
  </div>
);

const YamlImportDrawer = ({ open, onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [content, setContent] = useState("");
  const [filename, setFilename] = useState("");
  const [validation, setValidation] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  const [validateImport, { loading: validating }] = useMutation(
    graphql.mutations.VALIDATE_INSURANCE_PLAN_YAML_IMPORT,
  );
  const [executeImport, { loading: importing }] = useMutation(
    graphql.mutations.EXECUTE_INSURANCE_PLAN_YAML_IMPORT,
    {
      refetchQueries: [
        {
          query: graphql.queries.INSURANCE_PLANS,
          awaitRefetchQueries: true,
          variables: { ...INITIAL_VALUES },
        },
      ],
    },
  );

  const previewColumns = [
    {
      title: t("YAML_IMPORT.COLUMNS.IDENTIFIER"),
      dataIndex: "identifier",
      render: (identifier, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{identifier || "—"}</Text>
          <Text type="secondary">{row.name}</Text>
        </Space>
      ),
    },
    {
      title: t("YAML_IMPORT.COLUMNS.TYPE"),
      dataIndex: "isStandalone",
      render: (isStandalone) =>
        isStandalone ? (
          <Tag color={TAG_COLORS.BLUE}>
            {t("YAML_IMPORT.PLAN_TYPE.STANDALONE")}
          </Tag>
        ) : (
          <Tag>{t("YAML_IMPORT.PLAN_TYPE.MODULE")}</Tag>
        ),
    },
    {
      title: t("YAML_IMPORT.COLUMNS.STATE"),
      dataIndex: "isNew",
      render: (isNew) =>
        isNew ? (
          <Tag color={TAG_COLORS.GREEN}>
            {t("YAML_IMPORT.PLAN_STATE.NEW")}
          </Tag>
        ) : (
          <Tag color={TAG_COLORS.ORANGE}>
            {t("YAML_IMPORT.PLAN_STATE.EXISTING")}
          </Tag>
        ),
    },
    {
      title: t("YAML_IMPORT.COLUMNS.COVERAGES"),
      dataIndex: "coverageCount",
      align: "center",
    },
    {
      title: t("YAML_IMPORT.COLUMNS.MODULES"),
      dataIndex: "moduleCount",
      align: "center",
    },
    {
      title: t("YAML_IMPORT.COLUMNS.DOCUMENTS"),
      dataIndex: "documentCount",
      align: "center",
    },
  ];

  const renderPlanDetails = (record) => {
    const d = record.details || {};
    const elig = d.eligibility || {};
    return (
      <div className="yaml-import-drawer__details">
        <Line label={t("YAML_IMPORT.DETAILS.RANGE")}>
          <Text>{d.category || "—"}</Text>
          {d.level ? (
            <Text type="secondary">
              {" "}
              · {t("YAML_IMPORT.DETAILS.LEVEL", { level: d.level })}
            </Text>
          ) : null}
          {d.product ? (
            <Text type="secondary">
              {" "}
              · {t("YAML_IMPORT.DETAILS.PRODUCT", { product: d.product })}
            </Text>
          ) : null}
          {d.externalId ? (
            <Text type="secondary">
              {" "}
              · {t("YAML_IMPORT.DETAILS.CODE", { code: d.externalId })}
            </Text>
          ) : null}
          <Text type="secondary">
            {" "}
            ·{" "}
            {t("YAML_IMPORT.DETAILS.AGGREGATION", {
              aggregator: d.aggregator,
            })}
          </Text>
        </Line>

        <Line label={t("YAML_IMPORT.DETAILS.ELIGIBILITY")}>
          <Text>
            {t("YAML_IMPORT.DETAILS.AGE_RANGE", {
              min: elig.minAge ?? "?",
              max: elig.maxAge ?? "?",
            })}
          </Text>
          {(elig.socialRegimes || []).map((r) => (
            <Tag key={r}>{r}</Tag>
          ))}
          {(elig.profiles || []).map((p) => (
            <Tag key={p} color={TAG_COLORS.PURPLE}>
              {p}
            </Tag>
          ))}
        </Line>

        <Line label={t("YAML_IMPORT.DETAILS.PRICING")}>
          <Table
            size="small"
            pagination={false}
            rowKey={(c) => c.label}
            dataSource={d.coverages || []}
            columns={[
              {
                title: t("YAML_IMPORT.DETAILS.COVERAGE"),
                dataIndex: "label",
              },
              {
                title: t("YAML_IMPORT.DETAILS.MODE"),
                dataIndex: "mode",
                render: (m) => <Tag color={TAG_COLORS.GREEN}>{m}</Tag>,
              },
              {
                title: t("YAML_IMPORT.DETAILS.UNIT"),
                dataIndex: "unit",
              },
              {
                title: t("YAML_IMPORT.DETAILS.CELLS"),
                dataIndex: "cells",
                align: "center",
              },
              {
                title: t("YAML_IMPORT.DETAILS.SAMPLES"),
                dataIndex: "sample",
                render: (sample) => (
                  <Text
                    type="secondary"
                    className="yaml-import-drawer__sample-text"
                  >
                    {(sample || [])
                      .map((s) => `${s.key} = ${s.value}`)
                      .join("  ·  ")}
                  </Text>
                ),
              },
            ]}
          />
        </Line>

        {(d.modules || []).length > 0 && (
          <Line label={t("YAML_IMPORT.DETAILS.MODULES")}>
            {d.modules.map((m) => (
              <Tag key={m}>{m}</Tag>
            ))}
          </Line>
        )}

        {(d.documents || []).length > 0 && (
          <Line label={t("YAML_IMPORT.DETAILS.DOCUMENTS")}>
            {d.documents.map((doc) => (
              <Tag key={doc.name}>
                {doc.type} · {doc.name}
              </Tag>
            ))}
          </Line>
        )}
      </div>
    );
  };

  const reset = () => {
    setContent("");
    setFilename("");
    setValidation(null);
    setUpdateExisting(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setContent(String(reader.result));
      setFilename(file.name);
      setValidation(null);
    };
    reader.readAsText(file);
    return false;
  };

  const runValidate = async () => {
    const { data } = await validateImport({ variables: { data: { content } } });
    setValidation(get(data, "validateInsurancePlanYamlImport"));
  };

  const runImport = async () => {
    const { data } = await executeImport({
      variables: {
        data: {
          content,
          strategy: updateExisting
            ? YAML_IMPORT_STRATEGIES.UPDATE_CORE
            : YAML_IMPORT_STRATEGIES.NEW_ONLY,
        },
      },
    });
    const result = get(data, "executeInsurancePlanYamlImport");
    if (!get(result, "success")) {
      notification.error({
        message: t("YAML_IMPORT.IMPORT_FAILED"),
        description: (get(result, "errors") || []).map(formatError).join(" | "),
      });
      return;
    }
    notification.success({
      message: t("YAML_IMPORT.IMPORT_SUCCESS"),
      description: t("YAML_IMPORT.IMPORT_SUCCESS_DESC", {
        created: result.created,
        updated: result.updated,
        skipped: result.skipped,
      }),
    });
    close();
  };

  const errors = get(validation, "errors") || [];
  const warnings = get(validation, "warnings") || [];
  const plans = get(validation, "plans") || [];
  const canImport = get(validation, "valid") && plans.length > 0;

  return (
    <Drawer
      title={t("YAML_IMPORT.TITLE")}
      width={760}
      open={open}
      onClose={close}
      destroyOnClose
    >
      <Space
        direction="vertical"
        size="middle"
        className="yaml-import-drawer__content"
      >
        <Upload
          accept=".yaml,.yml,.txt"
          maxCount={1}
          beforeUpload={readFile}
          showUploadList={false}
        >
          <Button icon={<MdOutlineUploadFile size={18} />}>
            {t("YAML_IMPORT.CHOOSE_FILE")}
          </Button>
        </Upload>
        {filename && (
          <Text type="secondary">
            {t("YAML_IMPORT.FILE_LABEL", { name: filename })}
          </Text>
        )}

        <TextArea
          rows={12}
          value={content}
          placeholder={t("YAML_IMPORT.PLACEHOLDER")}
          onChange={(e) => {
            setContent(e.target.value);
            setValidation(null);
          }}
          className="yaml-import-drawer__textarea"
        />

        <Space>
          <Button
            type="primary"
            ghost
            loading={validating}
            disabled={!content.trim()}
            onClick={runValidate}
          >
            {t("YAML_IMPORT.VALIDATE")}
          </Button>
          <Checkbox
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
          >
            {t("YAML_IMPORT.UPDATE_EXISTING")}
          </Checkbox>
        </Space>

        {validation && (
          <>
            {canImport ? (
              <Alert
                type="success"
                showIcon
                message={t("YAML_IMPORT.VALID_SUCCESS", {
                  total: validation.totalPlans,
                  new: validation.newPlans,
                  existing: validation.existingPlans,
                })}
              />
            ) : (
              <Alert
                type="error"
                showIcon
                message={t("YAML_IMPORT.VALID_ERROR")}
              />
            )}

            {errors.length > 0 && (
              <Alert
                type="error"
                message={t("YAML_IMPORT.ERRORS")}
                description={
                  <ul className="yaml-import-drawer__error-list">
                    {errors.map((error) => (
                      <li key={formatError(error)}>{formatError(error)}</li>
                    ))}
                  </ul>
                }
              />
            )}

            {warnings.length > 0 && (
              <Alert
                type="warning"
                message={t("YAML_IMPORT.WARNINGS", { count: warnings.length })}
                description={
                  <ul className="yaml-import-drawer__error-list">
                    {warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                }
              />
            )}

            {plans.length > 0 && (
              <>
                <Title level={5} className="yaml-import-drawer__preview-title">
                  {t("YAML_IMPORT.PREVIEW")}
                </Title>
                <Text type="secondary">{t("YAML_IMPORT.PREVIEW_HINT")}</Text>
                <Table
                  size="small"
                  rowKey={(row) => row.identifier || row.name}
                  pagination={false}
                  dataSource={plans}
                  columns={previewColumns}
                  expandable={{
                    expandedRowRender: renderPlanDetails,
                    rowExpandable: (row) => !!row.details,
                  }}
                />
              </>
            )}

            <Space>
              <Button
                type="primary"
                loading={importing}
                disabled={!canImport}
                onClick={runImport}
              >
                {updateExisting
                  ? t("YAML_IMPORT.IMPORT")
                  : t("YAML_IMPORT.IMPORT_NEW")}
              </Button>
              <Button onClick={close}>{t("YAML_IMPORT.CANCEL")}</Button>
            </Space>
          </>
        )}
      </Space>
    </Drawer>
  );
};

export default YamlImportDrawer;
